import React from 'react';
import { Menu, Portal } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { useFloating, autoUpdate, offset, flip } from '@floating-ui/react';

// Define the shape of an action item
export interface ActionMenuItem {
    label: string;
    onClick: () => void;
    icon?: React.ComponentType<React.SVGProps<SVGSVGElement> & { className?: string }>;
    itemClassName?: string; // For the button/item itself
    iconClassName?: string; // For the icon
    disabled?: boolean;
}

interface ActionMenuProps {
    items: ActionMenuItem[];
    // Could add a prop for a custom trigger button in the future
    // triggerButton?: React.ReactNode;
}

export default function ActionMenu({ items }: ActionMenuProps) {
    const { refs, floatingStyles } = useFloating({
        placement: 'bottom-end',
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            flip(),
        ],
    });

    return (
        <Menu as="div" className="relative inline-block text-left">
            {({ open }) => (
                <>
                    <Menu.Button
                        ref={refs.setReference}
                        className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        // Disable button if all items are disabled or no items
                        disabled={items.length === 0 || items.every(item => item.disabled)}
                    >
                        <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
                    </Menu.Button>
                    {open && items.length > 0 && (
                        <Portal>
                            <Menu.Items
                                ref={refs.setFloating}
                                style={floatingStyles}
                                className="w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                                static // Keep items mounted for Floating UI
                            >
                                <div className="py-1">
                                    {items.map((item) => (
                                        <Menu.Item key={item.label} disabled={item.disabled}>
                                            {({ active, disabled }) => {
                                                const buttonClasses = [
                                                    'flex w-full items-center px-4 py-2 text-sm',
                                                    active && 'bg-gray-100',
                                                    item.itemClassName || 'text-gray-700',
                                                    disabled && 'opacity-50 cursor-not-allowed'
                                                ].filter(Boolean).join(' ');

                                                const iconClasses = [
                                                    'mr-3 h-5 w-5',
                                                    item.iconClassName || 'text-gray-400',
                                                    disabled && 'opacity-50'
                                                ].filter(Boolean).join(' ');

                                                return (
                                                    <button
                                                        onClick={item.onClick}
                                                        disabled={disabled}
                                                        className={buttonClasses}
                                                    >
                                                        {item.icon && (
                                                            <item.icon
                                                                className={iconClasses}
                                                            />
                                                        )}
                                                        {item.label}
                                                    </button>
                                                );
                                            }}
                                        </Menu.Item>
                                    ))}
                                </div>
                            </Menu.Items>
                        </Portal>
                    )}
                </>
            )}
        </Menu>
    );
} 